import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import api from "../api/axios";
import ProductCard from "../components/chat/ProductCard";
import CartSidebar from "../components/chat/CartSidebar";
import styles from "./ChatPage.module.css";

const SUGGESTIONS = [
  "Show me phones under ₹20,000",
  "Latest laptops available",
  "Is iPhone 15 in stock?",
  "Show my cart",
  "Where is my order?",
];

const SESSION_KEY = "shopbot_session_id";

export default function ChatPage() {
  const [messages, setMessages] = useState([{
    role: "bot",
    content: "Hi! I am your shopping assistant. Ask me about products, prices, availability, or place an order directly here.",
    products: [],
  }]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(() => localStorage.getItem(SESSION_KEY) || null);
  const [cart, setCart] = useState([]);
  const [showCart, setShowCart] = useState(false);
  const [cartLoading, setCartLoading] = useState(false);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  // On mount — restore cart from DB if session exists
  useEffect(() => {
    const savedSessionId = localStorage.getItem(SESSION_KEY);
    if (savedSessionId) {
      restoreSession(savedSessionId);
    }
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const restoreSession = async (sid) => {
    setCartLoading(true);
    try {
      const { data } = await api.get(`/chat/session/${sid}`);
      if (data.cart && data.cart.length > 0) {
        setCart(data.cart);
        toast.success(`Cart restored — ${data.cart.length} item(s)`, { duration: 2000 });
      }
      // Restore last few messages too
      if (data.messages && data.messages.length > 0) {
        const recent = data.messages.slice(-10).map(m => ({
          role: m.role,
          content: m.content,
          products: m.products || [],
          intent: m.intent,
        }));
        setMessages([
          {
            role: "bot",
            content: "Welcome back! Your previous session has been restored.",
            products: [],
          },
          ...recent,
        ]);
      }
    } catch {
      // Session expired or not found — clear it and start fresh
      localStorage.removeItem(SESSION_KEY);
      setSessionId(null);
    } finally {
      setCartLoading(false);
    }
  };

  const saveSession = (sid) => {
    setSessionId(sid);
    localStorage.setItem(SESSION_KEY, sid);
  };

  const sendMessage = async (text) => {
    const msg = text || input.trim();
    if (!msg || loading) return;
    setInput("");

    // Clear products from previous bot messages when new message is sent
    setMessages(prev => [
      ...prev.map(m => m.role === "bot" ? { ...m, products: [] } : m),
      { role: "user", content: msg },
    ]);

    setLoading(true);
    try {
      const { data } = await api.post("/chat/message", { message: msg, sessionId });
      saveSession(data.sessionId);
      setCart(data.cart || []);
      setMessages(prev => [...prev, {
        role: "bot",
        content: data.message.content,
        products: data.message.products || [],
        intent: data.message.intent,
      }]);
    } catch {
      toast.error("Failed to send message");
      setMessages(prev => [...prev, {
        role: "bot",
        content: "Sorry, something went wrong. Please try again.",
        products: [],
      }]);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleCheckout = async () => {
    if (cart.length === 0) return;
    try {
      const { data: order } = await api.post("/orders", {
        items: cart.map(i => ({ product: i.product, quantity: i.quantity })),
        shippingAddress: { street: "12 MG Road", city: "Kochi", state: "Kerala", pincode: "682001" },
        sessionId,
      });
      const { data: payment } = await api.post("/payment/create-checkout-session", { orderId: order._id });
      // Clear cart from localStorage after checkout
      localStorage.removeItem(SESSION_KEY);
      window.location.href = payment.url;
    } catch (err) {
      toast.error(err.response?.data?.message || "Checkout failed");
    }
  };

  const handleClearCart = async () => {
    if (!sessionId) return;
    try {
      await api.delete(`/chat/cart/${sessionId}`);
      setCart([]);
      toast.success("Cart cleared");
    } catch {
      toast.error("Failed to clear cart");
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.chatArea}>
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <div className={styles.botAvatar}>◈</div>
            <div>
              <div className={styles.botName}>ShopBot AI</div>
              <div className={styles.botStatus}><span className={styles.dot}/>Online</div>
            </div>
          </div>
          <button className={`btn btn-ghost ${styles.cartBtn}`} onClick={() => setShowCart(true)}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/>
              <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
            </svg>
            Cart
            {cartLoading
              ? <span className="spinner" style={{width:"12px",height:"12px",marginLeft:"2px"}}/>
              : cart.length > 0 && <span className={styles.cartCount}>{cart.length}</span>
            }
          </button>
        </div>

        <div className={styles.messages}>
          {messages.map((msg, i) => (
            <div key={i}>
              <div className={`${styles.msgWrap} ${msg.role === "user" ? styles.userWrap : styles.botWrap}`}>
                {msg.role === "bot" && <div className={styles.botAvatarSmall}>◈</div>}
                <div className={`${styles.bubble} ${msg.role === "user" ? styles.userBubble : styles.botBubble}`}>
                  <p className={styles.msgText}>{msg.content}</p>
                  {msg.intent === "payment_ready" && (
                    <button className={`btn btn-primary ${styles.checkoutBtn}`} onClick={handleCheckout}>
                      Proceed to Checkout
                    </button>
                  )}
                </div>
                {msg.role === "user" && <div className={styles.userAvatarSmall}>U</div>}
              </div>

              {msg.products?.length > 0 && (
                <div className={styles.productsRow}>
                  {msg.products.slice(0, 4).map(p => (
                    <ProductCard
                      key={p._id}
                      product={p}
                      onOrder={() => sendMessage(`I want to order ${p.name}`)}
                    />
                  ))}
                </div>
              )}
            </div>
          ))}

          {loading && (
            <div className={`${styles.msgWrap} ${styles.botWrap}`}>
              <div className={styles.botAvatarSmall}>◈</div>
              <div className={`${styles.bubble} ${styles.botBubble} ${styles.typing}`}>
                <span/><span/><span/>
              </div>
            </div>
          )}
          <div ref={bottomRef} />
        </div>

        <div className={styles.suggestions}>
          {SUGGESTIONS.map(s => (
            <button key={s} className={styles.suggestion} onClick={() => sendMessage(s)}>{s}</button>
          ))}
        </div>

        <div className={styles.inputArea}>
          <input
            ref={inputRef}
            className={styles.chatInput}
            placeholder="Ask about products, prices, or place an order..."
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === "Enter" && !e.shiftKey && sendMessage()}
          />
          <button
            className={`btn btn-primary ${styles.sendBtn}`}
            onClick={() => sendMessage()}
            disabled={loading || !input.trim()}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <line x1="22" y1="2" x2="11" y2="13"/>
              <polygon points="22 2 15 22 11 13 2 9 22 2"/>
            </svg>
          </button>
        </div>
      </div>

      {showCart && (
        <CartSidebar
          cart={cart}
          onClose={() => setShowCart(false)}
          onCheckout={handleCheckout}
          onClear={handleClearCart}
        />
      )}
    </div>
  );
}
